package com.partejar.app.feature.agenda.presentation

import com.partejar.app.feature.agenda.data.AgendaEvent
import com.partejar.app.feature.agenda.data.AgendaPeriod
import com.partejar.app.feature.agenda.data.endDateTime
import com.partejar.app.feature.agenda.data.startDate
import com.partejar.app.feature.agenda.data.startDateTime
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.plus

internal data class AgendaDayGroup(
    val date: LocalDate,
    val events: List<AgendaEvent>
)

internal fun agendaDates(period: AgendaPeriod): List<LocalDate> {
    val start = runCatching { LocalDate.parse(period.startDate) }.getOrNull() ?: return emptyList()
    val end = runCatching { LocalDate.parse(period.endDate) }.getOrNull() ?: return emptyList()
    if (end < start) return emptyList()
    return buildList {
        var date = start
        while (date <= end) {
            add(date)
            date = date.plus(1, DateTimeUnit.DAY)
        }
    }
}

internal fun agendaDayGroups(
    events: List<AgendaEvent>,
    selectedDate: LocalDate
): List<AgendaDayGroup> {
    val byDate = events.mapNotNull { event -> event.startDate()?.let { it to event } }
        .groupBy({ it.first }, { it.second })
    return (byDate.keys + selectedDate)
        .sorted()
        .map { date -> AgendaDayGroup(date, byDate[date].orEmpty().sortedBy(AgendaEvent::startsAt)) }
}

internal fun nextAgendaEventId(
    events: List<AgendaEvent>,
    now: LocalDateTime
): Long? = events.asSequence()
    .mapNotNull { event -> event.startDateTime()?.let { it to event.id } }
    .filter { (startsAt) -> startsAt >= now }
    .minByOrNull { (startsAt) -> startsAt }
    ?.second

internal fun agendaDayHeading(date: LocalDate, today: LocalDate): String {
    val prefix = when (date) {
        today -> "Hoje"
        today.plus(1, DateTimeUnit.DAY) -> "Amanhã"
        else -> agendaWeekdayLong(date.dayOfWeek)
    }
    return "$prefix · ${date.dayOfMonth.toString().padStart(2, '0')} de ${agendaMonthName(date.monthNumber)}"
}

internal fun agendaMonthHeading(date: LocalDate): String =
    "${agendaMonthName(date.monthNumber).uppercase()} ${date.year}"

internal fun agendaWeekdayShort(day: DayOfWeek): String = when (day) {
    DayOfWeek.MONDAY -> "seg"
    DayOfWeek.TUESDAY -> "ter"
    DayOfWeek.WEDNESDAY -> "qua"
    DayOfWeek.THURSDAY -> "qui"
    DayOfWeek.FRIDAY -> "sex"
    DayOfWeek.SATURDAY -> "sáb"
    DayOfWeek.SUNDAY -> "dom"
}

internal fun agendaEventTime(event: AgendaEvent): String = event.startDateTime()
    ?.let { "${it.hour.toString().padStart(2, '0')}:${it.minute.toString().padStart(2, '0')}" }
    ?: "--:--"

internal fun agendaEventTimeRange(event: AgendaEvent): String {
    val start = agendaEventTime(event)
    val end = event.endDateTime()?.let {
        "${it.hour.toString().padStart(2, '0')}:${it.minute.toString().padStart(2, '0')}"
    }
    return if (end.isNullOrBlank()) start else "$start – $end"
}

internal fun agendaEventType(type: String): String? = when (type) {
    "REUNIAO_APRESENTACAO" -> "Reunião de apresentação"
    "OFICINA_PREPARACAO" -> "Oficina de preparação"
    "OFICINA_AMAMENTACAO" -> "Oficina de amamentação"
    "OFICINA_CUIDADOS_RN" -> "Oficina de cuidados com RN"
    "OUTRO" -> "Compromisso da equipe"
    else -> null
}

private fun agendaWeekdayLong(day: DayOfWeek): String = when (day) {
    DayOfWeek.MONDAY -> "Segunda"
    DayOfWeek.TUESDAY -> "Terça"
    DayOfWeek.WEDNESDAY -> "Quarta"
    DayOfWeek.THURSDAY -> "Quinta"
    DayOfWeek.FRIDAY -> "Sexta"
    DayOfWeek.SATURDAY -> "Sábado"
    DayOfWeek.SUNDAY -> "Domingo"
}

private fun agendaMonthName(month: Int): String = listOf(
    "janeiro", "fevereiro", "março", "abril", "maio", "junho",
    "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"
).getOrElse(month - 1) { "" }
